import { useMemo, useState } from "react";
import { GrButton, GrCheckbox, GrDialog, GrDialogProps, GrDivider, GrIcon, GrStack, GrTextField, GrTypography } from "@jnpmedi/grebob";
import isIP from "validator/lib/isIP";
import { MvnMessage } from "@newLibs/utils";
import { notify } from "@newHooks/context/snackbar/notify";
import { useUpdateFolderSharingSettingMutation } from "@newHooks/mutation/folderSharingSetting/updateFolderSharingSetting.mutation";
import { useFolderSharingSettingQuery } from "@newHooks/query/folderSharingSetting/folderSharingSetting.query";
import { SharingRoleGuard } from "./SharingRoleGuardView";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { FolderSharingRole } from "@maven-rest/docs/FolderSharing.dto";
import { FolderSharingIpWhitelist, FolderSharingSetting } from "@maven-rest/docs/FolderSharingSetting.dto";

interface AdvancedSharingSettingsDialogProps {
  visibility: boolean;
  organizationId: bigint;
  folderId?: bigint;
  fileId?: bigint;
  size?: GrDialogProps["size"];
  setVisibility: (v: boolean) => void;
}

export function AdvancedSharingSettingsDialog({
  visibility,
  organizationId,
  folderId,
  fileId,
  size = "xs",
  setVisibility,
}: AdvancedSharingSettingsDialogProps) {
  const [isDynamicWatermarkEnabled, setIsDynamicWatermarkEnabled] = useState<boolean>();
  const [isIpWhitelistEnabled, setIsIpWhitelistEnabled] = useState<boolean>();
  const [isDownloadWithWatermarkEnabled, setIsDownloadWithWatermarkEnabled] = useState<boolean>();
  const [ipWhitelistAddDialogVisibility, setIpWhitelistAddDialogVisibility] = useState<boolean>(false);
  const [ipWhitelists, setIpWhitelists] = useState<Array<IpWhitelist>>();
  const [folderSharingSetting, setFolderSharingSetting] = useState<FolderSharingSetting>();

  const confirmDisabled =
    isDynamicWatermarkEnabled === undefined &&
    isDownloadWithWatermarkEnabled === undefined &&
    isIpWhitelistEnabled === undefined &&
    !ipWhitelists?.length;

  const close = () => {
    setVisibility(false);
    setIsDynamicWatermarkEnabled(undefined);
    setIsIpWhitelistEnabled(undefined);
    setIsDownloadWithWatermarkEnabled(undefined);
    setIpWhitelists(undefined);
    setFolderSharingSetting(undefined);
  };

  const { isLoading: isFolderSharingSettingLoading } = useFolderSharingSettingQuery(
    { organizationId, folderId, fileId },
    {
      onSuccess: (data) => {
        setFolderSharingSetting(data.setting);
        const { isDownloadWithWatermarkEnabled, isDynamicWatermarkEnabled, isIpWhitelistEnabled } = data.setting;

        setIsIpWhitelistEnabled(isIpWhitelistEnabled);
        setIsDownloadWithWatermarkEnabled(isDownloadWithWatermarkEnabled);
        setIsDynamicWatermarkEnabled(isDynamicWatermarkEnabled);
      },
    }
  );

  const { mutate: updateFolderSharingSettingRequest } = useUpdateFolderSharingSettingMutation({
    onSuccess: () => {
      close();

      notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_UPDATE_SUCC), "SUCC");
    },
    onError: (error) =>
      notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_UPDATE_FAIL, { message: error.responseMessage }), "ERROR"),
  });

  const availableIpWhiteListMemo = useMemo(
    () => mergeIpWhitelists(folderSharingSetting?.ipWhitelists, ipWhitelists),
    [folderSharingSetting?.ipWhitelists, ipWhitelists]
  );

  const handleUpdateFolderSharingSetting = () => {
    updateFolderSharingSettingRequest({
      organizationId,
      folderId,
      fileId,
      isIpWhitelistEnabled,
      isDynamicWatermarkEnabled,
      isDownloadWithWatermarkEnabled,
      ipWhitelists,
    });
  };

  return (
    <>
      <NewIpWhitelistAddDialog
        visibility={ipWhitelistAddDialogVisibility}
        setVisibility={setIpWhitelistAddDialogVisibility}
        ipWhitelists={ipWhitelists || []}
        setIpWhitelists={setIpWhitelists}
      />
      <GrDialog
        title="Advanced settings"
        open={visibility}
        size={size}
        onClose={close}
        onConfirm={handleUpdateFolderSharingSetting}
        cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
        confirmButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_SAVE_NOUN), disabled: confirmDisabled }}
      >
        <SharingRoleGuard folderId={folderId} fileId={fileId} role={FolderSharingRole.Manager}>
          {() => (
            <>
              {!isFolderSharingSettingLoading && folderSharingSetting && (
                <GrStack flexDirection="column">
                  <GrCheckbox
                    id="dynamic-watermark"
                    label={{
                      text: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_ENABLE_DYNAMIC_WATERMARK),
                      htmlFor: "dynamic-watermark",
                    }}
                    checked={isDynamicWatermarkEnabled}
                    onChange={(_, checked) => setIsDynamicWatermarkEnabled(checked)}
                  />
                  {process.env.REACT_APP_FEATURE_VIEWER_DOWNLOAD_WATERMARK === "true" && (
                    <GrCheckbox
                      id="download-watermark"
                      label={{
                        text: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_ENABLE_DOWNLOAD_WITH_WATERMARK),
                        htmlFor: "download-watermark",
                      }}
                      checked={isDownloadWithWatermarkEnabled}
                      onChange={(_, checked) => setIsDownloadWithWatermarkEnabled(checked)}
                    />
                  )}
                  <GrCheckbox
                    id="ip-whitelist"
                    label={{
                      text: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_ENABLE_IP_WHITELIST),
                      htmlFor: "ip-whitelist",
                    }}
                    checked={isIpWhitelistEnabled}
                    onChange={(_, checked) => setIsIpWhitelistEnabled(checked)}
                  />
                  {isIpWhitelistEnabled && (
                    <GrStack gap={2} padding="8px 11px 0px">
                      <GrDivider
                        variant="inset"
                        orientation="vertical"
                        style={{ width: "1px", height: "inherit", borderRightWidth: "inherit" }}
                      />

                      <GrStack flexDirection="column" gap={2} justifyContent="flex-start">
                        {!!availableIpWhiteListMemo.length &&
                          availableIpWhiteListMemo.map((ipAddr) => (
                            <GrCheckbox
                              key={`${ipAddr.cidr}`}
                              label={{ text: `${ipAddr.name} (${ipAddr.cidr})` }}
                              disabled={
                                typeof isIpWhitelistEnabled === "boolean"
                                  ? !isIpWhitelistEnabled
                                  : !folderSharingSetting.isIpWhitelistEnabled
                              }
                              checked={ipAddr.isEnabled}
                              onChange={(_, checked) =>
                                setIpWhitelists(updateIpWhitelist(availableIpWhiteListMemo, { ...ipAddr, isEnabled: checked }))
                              }
                            />
                          ))}
                        <GrButton
                          color="grey"
                          variant="outlined"
                          startIcon="plus"
                          startIconSize={16}
                          style={{ justifyContent: "flex-start", marginLeft: "6px" }}
                          size="medium"
                          onClick={() => setIpWhitelistAddDialogVisibility(true)}
                        >
                          <GrTypography type="Content.Subtitle03" color="grey100">
                            {MvnMessage("Add IP whitelist")}
                          </GrTypography>
                        </GrButton>
                      </GrStack>
                    </GrStack>
                  )}
                </GrStack>
              )}
            </>
          )}
        </SharingRoleGuard>
      </GrDialog>
    </>
  );
}

type IpWhitelist = FolderSharingIpWhitelist & { added?: boolean };

interface NewIpWhitelistAddDialogProps {
  visibility: boolean;
  ipWhitelists: IpWhitelist[];
  size?: GrDialogProps["size"];
  setVisibility: (v: boolean) => void;
  setIpWhitelists: (v: IpWhitelist[]) => void;
}

function NewIpWhitelistAddDialog({ visibility, ipWhitelists, size = "xs", setVisibility, setIpWhitelists }: NewIpWhitelistAddDialogProps) {
  const [name, setName] = useState<string>("");
  const [cidr, setCidr] = useState<string>("");

  const handleConfirm = () => {
    setIpWhitelists(updateIpWhitelist(ipWhitelists, { name, cidr, isEnabled: true, added: true }));
    setVisibility(false);
    setName("");
    setCidr("");
  };

  const isInvalidCidr = !!cidr && !isIP(cidr);

  return (
    <GrDialog
      title="Add IP Whitelist"
      open={visibility}
      onClose={() => setVisibility(false)}
      size={size}
      onConfirm={handleConfirm}
      confirmButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_ADD_NOUN), disabled: !isIP(cidr) }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
    >
      <GrStack gap={5} flexDirection="column">
        <GrTextField
          fullWidth
          label={{ text: "Name" }}
          placeholder="Enter name"
          value={name}
          onChange={(evt) => setName(evt.target.value)}
        />
        <GrTextField
          fullWidth
          label={{ text: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_IP_WHITELIST_IP_ADDR_LABEL) }}
          placeholder={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_SHARING_SETTINGS_IP_WHITELIST_IP_ADDR_PLACEHOLDER)}
          value={cidr}
          maxLength={20}
          error={isInvalidCidr}
          endAdornment={isInvalidCidr ? <GrIcon size={16} color="red060" type="alert-triangle-filled" /> : undefined}
          onChange={(evt) => setCidr(evt.target.value)}
        />
      </GrStack>
    </GrDialog>
  );
}

const updateIpWhitelist = (ipWhitelists: IpWhitelist[], ipWhitelist: IpWhitelist): IpWhitelist[] => {
  return ipWhitelists
    .filter((e) => e.cidr !== ipWhitelist.cidr)
    .concat([ipWhitelist])
    .sort((a, b) => a.name.localeCompare(b.name));
};

const mergeIpWhitelists = (base: IpWhitelist[] | undefined, target: IpWhitelist[] | undefined): IpWhitelist[] => {
  return (target || []).reduce((p, e) => updateIpWhitelist(p, e), base || []);
};
